import { useEffect, useMemo, useState } from "react";
import type { Language } from "@/types/theme";
import { db } from "@/api/firebase";
import { doc, getDoc } from "firebase/firestore";

/**
 * Determines the preferred content language for a site visitor.
 *
 * The hook prioritises URL overrides, then site metadata, and finally a configurable fallback.
 * It returns the resolved language alongside setters and metadata for components to adjust UI
 * accordingly.
 *
 * @param siteId - Current site identifier used to look up default language metadata.
 * @param urlLanguageParam - Optional language override sourced from routing parameters.
 * @param fallback - Language to use when no other source resolves (defaults to English).
 * @returns Resolved language, setter, and boolean indicating whether a URL override is in effect.
 */
type PreferredLanguageOptions = {
  defaultLanguage?: Language;
};

export const usePreferredLanguage = (
  siteId: string | undefined,
  urlLanguageParam?: string | null,
  fallback: Language = "en",
  options?: PreferredLanguageOptions
) => {
  const hasUrlParam = useMemo(() => !!urlLanguageParam, [urlLanguageParam]);
  const initialLanguage = (urlLanguageParam ||
    options?.defaultLanguage ||
    fallback) as Language;
  const [language, setLanguage] = useState<Language>(initialLanguage);

  useEffect(() => {
    if (!siteId || hasUrlParam || options?.defaultLanguage) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "sites", siteId));
        if (snap.exists()) {
          const meta = snap.data() as any;
          const defaultLang = meta?.defaultLanguage as string | undefined;
          if (!cancelled && defaultLang && defaultLang !== language) {
            setLanguage(defaultLang as Language);
          }
        }
      } catch {
        // ignore metadata errors; fallback already applied
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId, hasUrlParam, language]);

  return { language, setLanguage, hasUrlParam } as const;
};
